import axios from 'axios';
import auth from '../cognito-auth/cognitoAuth';
import { loggerService } from './LoggerService';

const baseURL = process.env.VUE_APP_CONSUMPTION_DEVICES_URL;
const apiKey = process.env.VUE_APP_CONSUMPTION_DEVICES_API_KEY;
const commissioningApiURL = process.env.VUE_APP_COMMISSIONING_API_URL;
const exportURL = process.env.VUE_APP_CONSUMPTION_DEVICES_EXPORT_URL
  ? process.env.VUE_APP_CONSUMPTION_DEVICES_EXPORT_URL
  : commissioningApiURL
  ? `${commissioningApiURL.replace(/\/$/, '')}/consumption-devices/export`
  : '';

const SimpleConsumptionDevicesService = axios.create(createAxiosConfig(false));

function createAxiosConfig(isUserSpecific) {
  const headers = {
    'Content-Type': 'application/json',
  };
  if (!isUserSpecific) {
    headers['x-api-key'] = apiKey;
  } else {
    headers['Authorization'] = auth.auth
      .getSignInUserSession()
      .getIdToken().jwtToken;
  }
  return {
    baseURL,
    headers,
  };
}

/**
 * @param { { end_date: string | null, from_index: number, limit: number, status: string, search_word: string, start_date: string | null } } requestBody
 * @returns
 */
export async function getConsumptionDevicesOrdersWithFilter(requestBody) {
  try {
    const data = JSON.stringify({
      end_date: requestBody.end_date, //null,
      from_index: requestBody.from_index, //0,
      limit: requestBody.limit, //10,
      status: requestBody.status, //'',
      search_word: requestBody.search_word, //'',
      start_date: requestBody.start_date, //null,
    });
    const response = await axios.post(
      'items-with-filter',
      data,
      createAxiosConfig(true)
    );
    return response.data;
  } catch (error) {
    loggerService.error(error);
    return null;
  }
}

/**
 * Loads one page of the SteuVE export. Throws on failure so the caller can retry or abort.
 * @param { { end_date: string | null, status: string, search_word: string, start_date: string | null } } requestBody
 * @param { { cursor?: string | null, signal?: AbortSignal } } options
 * @returns { Promise<{ count: number, items: object[], next_cursor: string | null }> }
 */
export async function exportConsumptionDevicesOrders(
  requestBody,
  { cursor = null, signal } = {}
) {
  try {
    if (!exportURL) {
      throw new Error('Missing VUE_APP_CONSUMPTION_DEVICES_EXPORT_URL');
    }

    const requestConfig = createAxiosConfig(true);
    const token = requestConfig.headers.Authorization;
    requestConfig.headers.Authorization = token?.startsWith('Bearer ')
      ? token
      : `Bearer ${token}`;
    requestConfig.signal = signal;

    const data = JSON.stringify({
      end_date: requestBody.end_date,
      status: requestBody.status,
      search_word: requestBody.search_word,
      start_date: requestBody.start_date,
      cursor,
    });

    const response = await axios.post(exportURL, data, requestConfig);
    return response.data;
  } catch (error) {
    if (!axios.isCancel(error)) {
      loggerService.error(error);
    }
    throw error;
  }
}

export async function getConsumptionDevicesSummaryForPipeline() {
  try {
    const response = await SimpleConsumptionDevicesService.get(
      'summary-for-pipeline'
    );
    return response.data;
  } catch (error) {
    loggerService.error(error);
    return null;
  }
}

export async function getConsumptionDevicesSummaryForDiagram() {
  try {
    const response = await SimpleConsumptionDevicesService.get(
      'summary-for-diagram'
    );
    return response.data;
  } catch (error) {
    loggerService.error(error);
    return null;
  }
}

export async function getConsumptionDevicesOrderById(orderId) {
  try {
    const response = await axios.get(
      `get-by-id/${orderId}`,
      createAxiosConfig(true)
    );
    return response.data;
  } catch (error) {
    loggerService.error(error);
    return null;
  }
}

/**
 * Updates status of a consumption devices order
 * @param { { id: string, remark?: string, old_value: { status: string }, new_value: { status: string } } } requestObject
 * @param { string } user
 * @returns { Promise<{ data: { updateCDStatus { id: string, changed_by: string, event_origin: string, created: Date, field: string, old_value: string, new_value: string, note: string | null } } } | null> }
 */
export async function updateConsumptionDevicesOrderStatus(requestObject, user) {
  return await update('updateCDStatus', requestObject, user);
}

export async function updateCDControllableDevicesPerformance(
  requestObject,
  user
) {
  return await update(
    'updateCDControllableDevicesPerformance',
    requestObject,
    user
  );
}

export async function updateCDControllableDevicesCounter(requestObject, user) {
  return await update(
    'updateCDControllableDevicesCounter',
    requestObject,
    user
  );
}

export async function updateCDControllableDevicesInstaller(
  requestObject,
  user
) {
  return await update(
    'updateCDControllableDevicesInstaller',
    requestObject,
    user
  );
}

export async function updateCDControllableDevicesCommonTechnicalData(
  requestObject,
  user
) {
  return await update(
    'updateCDControllableDevicesCommonTechnicalData',
    requestObject,
    user
  );
}

/**
 * Updates contact data of a consumption device order
 * @param { { id: string, user_id: string, note: string, old_value: { applicant: { legal_title: string, title: string, city_name: string, company_name: string, name:string, surname: string, phone_number:string, additional_phone_number: string, email: string, preferred_contact_channel: string, address: { street: string, street_number: string, zip: string, city: string, country: string } }, applicant_role: string, end_customer: { legal_title: string, title: string, city_name: string, company_name: string, name:string, surname: string, phone_number:string, additional_phone_number: string, email: string, preferred_contact_channel: string, address: { street: string, street_number: string, zip: string, city: string, country: string } } | null }, new_value: { applicant: { legal_title: string, title: string, city_name: string, company_name: string, name:string, surname: string, phone_number:string, additional_phone_number: string, email: string, preferred_contact_channel: string, address: { street: string, street_number: string, zip: string, city: string, country: string } }, applicant_role: string, end_customer: { legal_title: string, title: string, city_name: string, company_name: string, name:string, surname: string, phone_number:string, additional_phone_number: string, email: string, preferred_contact_channel: string, address: { street: string, street_number: string, zip: string, city: string, country: string } } | null } } } requestObject
 * @param { string } user
 * @returns { Promise<{ data: updateCDContactData { id: string, changed_by: string, event_origin: string, created: Date, field: string, old_value: string, new_value: string, note: string | null } } } | null> }
 */
export async function updateConsumptionDevicesOrderContactData(
  requestObject,
  user
) {
  return await update('updateCDContactData', requestObject, user);
}

/**
 * Updates contact data of a consumption device order
 * @param { { id: string, user_id: string, note: string, old_value: { address: { street: string, street_number: string, zip: string, city: string, country: string } }, new_value: { address: { street: string, street_number: string, zip: string, city: string, country: string } } } requestObject
 * @param { string } user
 * @returns { Promise<{ data: { updateCDAddress : { id: string, changed_by: string, event_origin: string, created: Date, field: string, old_value: string, new_value: string, note: string | null } } } | null> }
 */
export async function updateConsumptionDevicesOrderAddress(
  requestObject,
  user
) {
  return await update('updateCDAddress', requestObject, user);
}

export async function updateCDBillingModule(requestObject, user) {
  return await update('updateCDBillingModule', requestObject, user);
}

export async function updateCDMeasureConcept(requestObject, user) {
  return await update('updateCDMeasureConcept', requestObject, user);
}

export async function updateCDControllableDevicesBatteryStorageSpecificData(
  requestObject,
  user
) {
  return await update(
    'updateCDControllableDevicesBatteryStorageSpecificData',
    requestObject,
    user
  );
}

export async function updateCDControllableDevicesWallboxSpecificData(
  requestObject,
  user
) {
  return await update(
    'updateCDControllableDevicesWallboxSpecificData',
    requestObject,
    user
  );
}

export async function updateCDControllableDevicesWallboxCoordinates(
  requestObject,
  user
) {
  return await update(
    'updateCDControllableDevicesWallboxCoordinates',
    requestObject,
    user
  );
}

/**
 * @param { { id: string, limit: Int, from_index: Int } } requestBody
 * @returns { Promise<{ data: { consumptionDevicesDocumentsHistory: { count_changes: Int, changes: any[] } } }> }
 */
export async function getConsumptionDevicesOrderDocumentsHistory(requestBody) {
  try {
    const data = JSON.stringify({
      id: requestBody.id,
      from_index: requestBody.from_index,
      limit: requestBody.limit,
    });
    const response = await axios.post(
      'document-history',
      data,
      createAxiosConfig(true)
    );
    return response.data;
  } catch (error) {
    loggerService.error(error);
    return [];
  }
}

/**
 * @param { { id: string, limit: Int, from_index: Int } } requestBody
 * @returns { Promise<{ data: { consumptionDevicesHistory: { count_changes: Int, changes: any[] } } }> }
 */
export async function getConsumptionDevicesOrderHistory(requestBody) {
  try {
    const data = JSON.stringify({
      id: requestBody.id,
      from_index: requestBody.from_index,
      limit: requestBody.limit,
    });
    const response = await axios.post('history', data, createAxiosConfig(true));
    return response.data;
  } catch (error) {
    loggerService.error(error);
    return [];
  }
}

/**
 * @param { { id: string, title: string, note: string, user_id: string, filename?: string } } requestObject
 * @param { string } user
 * @returns { Promise<{ data: { addConsumptionDeviceNote: { _persistent_event_id_: string, id: string, changed_by: string, event_origin: string, created: Date, title: string, field: string, note: string, filename?: string } } } | null>
 */
export async function addConsumptionDevicesOrderNote(requestObject, user) {
  try {
    const data = JSON.stringify({
      id: requestObject.id,
      payload: requestObject,
      event_origin: 'BACKOFFICE',
      changed_by: user,
    });
    const response = await axios.post(
      'add-note',
      data,
      createAxiosConfig(true)
    );
    return response.data;
  } catch (error) {
    loggerService.error('addConsumptionDevicesOrderNote failed', error);
    return null;
  }
}

export async function updateConsumptionDeviceTechnicalInspectionData(
  requestObject,
  user
) {
  return await update(
    'updateConsumptionDeviceTechnicalInspectionData',
    requestObject,
    user
  );
}

async function update(field, requestObject, user) {
  try {
    const data = JSON.stringify({
      id: requestObject.id,
      payload: requestObject,
      event_origin: 'BACKOFFICE',
      changed_by: user,
    });
    const response = await axios.post(
      `update/${field}`,
      data,
      createAxiosConfig(true)
    );
    return response.data;
  } catch (error) {
    loggerService.error(`Update failed on ${field}`, error);
    return null;
  }
}
